// Pure helpers for a route stop's place: State -> District -> City, or "Other"
// with an area typed by hand. No React and no I/O.
//
// A place, as the pickers hand it to a screen:
//   { stateId, state, districtId, district, cityId, city, kind }
// `city` is the name shown for it: the listed town's name (kind "city", with its
// `cityId`) or the area the user typed (kind "area", cityId null). Ids are
// strings (they are <select> values); places that came from the server (or from
// before districts existed) may have no district.

export const OTHER_CITY = "__other__";
export const PLACE_KIND = Object.freeze({ city: "city", area: "area" });

export const tidyName = (text) => (text || "").replace(/\s+/g, " ").trim();

// "Bagru, Jaipur, Rajasthan" — the district only when it adds something (not
// "Jaipur, Jaipur, Rajasthan"), and no district for older places without one.
// Same rule as the server's label, so a stop reads the same before and after saving.
export function placeLabel({ city, district, state }) {
  const parts = [city];
  if (district && district.toLowerCase() !== String(city).toLowerCase()) parts.push(district);
  if (state) parts.push(state);
  return parts.join(", ");
}

// Are these two stops the same place? The server keeps one place per district +
// name (ignoring case), so an area typed under "Other" that equals a listed town
// IS that town, while the same name in two districts is two places. A place from
// before districts existed has no district: it counts as the same as a
// same-named place in its state, whatever that place's district.
export function samePlace(a, b) {
  if (tidyName(a.city).toLowerCase() !== tidyName(b.city).toLowerCase() || a.stateId !== b.stateId) return false;
  return !a.districtId || !b.districtId || a.districtId === b.districtId;
}

// A newly added stop as the API's route item.
export function placeToRouteItem(place) {
  if (place.districtId) {
    const base = { state_id: Number(place.stateId), district_id: Number(place.districtId) };
    return place.kind === PLACE_KIND.area || !place.cityId ? { ...base, area_name: place.city } : { ...base, city_id: Number(place.cityId) };
  }
  return { state_id: Number(place.stateId), city_name: place.city };
}

// A stop already on the saved route (the API's route row) as a place.
export function placeFromRouteRow(row) {
  return {
    stateId: String(row.state_id),
    state: row.state_name,
    districtId: row.district_id ? String(row.district_id) : "",
    district: row.district_name || "",
    cityId: "",
    city: row.city_name,
    kind: PLACE_KIND.city,
  };
}
